import {
    ApprovalExpenseGet,
    ApprovalInvoiceGet,
    Decimal,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, computed } from "@preact/signals";
import { dateFromApi } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createEmitter, createListController } from "../utils";
import {
    ApprovalExpense,
    ApprovalInvoice,
    ApproveEntityType,
    ApproveFilter,
    ApproveParams,
    ApprovePrivateFilter,
} from "./interfaces";
import {
    getEntityId,
    getFilterKey,
    getPrivateFilterKey,
    splitCompanies,
} from "./utils";

export const makeApproval = ({ connection }: ApiSliceParams) => {
    const { emit, on, off } = createEmitter<
        ApprovalExpense | ApprovalInvoice
    >();
    const loadingStore: Record<string, ReadonlySignal<boolean>> = {};
    const loadedStore: Record<string, ReadonlySignal<boolean>> = {};
    const {
        items: expenses,
        listen: listenExpenses,
        loading: loadingExpenses,
        loaded: loadedExpenses,
    } = createListController<
        ApprovalExpenseGet,
        ApprovalExpense,
        ApprovePrivateFilter
    >({
        connection,
        list: (con, { status, companies }) =>
            con.approve.expense.approval.itemsBatch({
                status,
                company: companies,
            }),
        getKey: getPrivateFilterKey,
        getId: (e) => getEntityId("expense", e),
        view: (item) => ({
            ...item,
            _id: item.id,
            id: getEntityId("expense", item),
            date: dateFromApi(item.date),
        }),
        emit,
        name: "approvalExpense",
    });

    const {
        items: invoices,
        listen: listenInvoices,
        loading: loadingInvoices,
        loaded: loadedInvoices,
    } = createListController<
        ApprovalInvoiceGet,
        ApprovalInvoice,
        ApprovePrivateFilter
    >({
        connection,
        list: (con, { status, companies }) =>
            con.approve.invoice.approval.itemsBatch({
                status,
                company: companies,
            }),
        getKey: getPrivateFilterKey,
        getId: (e) => getEntityId("invoice", e),
        view: (item) => ({
            ...item,
            _id: item.id,
            id: getEntityId("invoice", item),
            date: dateFromApi(item.date),
        }),
        emit,
        name: "approvalInvoice",
    });

    const myExpenses = computed(() =>
        expenses.value.filter(
            (e) => e.user === connection.value.user.preferred_username,
        ),
    );
    const myInvoices = computed(() =>
        invoices.value.filter(
            (i) => i.user === connection.value.user.preferred_username,
        ),
    );
    const myAll = computed(() => [...myExpenses.value, ...myInvoices.value]);
    const mine = {
        expenses: myExpenses,
        invoices: myInvoices,
        all: myAll,
    };
    const all = computed(() => [...expenses.value, ...invoices.value]);

    const listen = (
        filterInvoice: ApprovePrivateFilter,
        filterExpense: ApprovePrivateFilter,
    ) => {
        listenExpenses(filterExpense);
        listenInvoices(filterInvoice);
    };

    const approveInvoice = ({
        id,
        parent,
        company,
        amount,
        comment,
        kind,
    }: ApproveParams) =>
        connection.value.approve.invoice.approval.approve({
            id,
            parent,
            company,
            amount: Decimal.from(amount),
            comment,
            kind,
        });

    const approveExpense = ({
        id,
        parent,
        company,
        amount,
        comment,
        kind,
    }: ApproveParams) =>
        connection.value.approve.expense.approval.approve({
            id,
            parent,
            company,
            amount: Decimal.from(amount),
            comment,
            kind,
        });

    const loading = (filter: ApproveFilter, type: ApproveEntityType) => {
        const key = type + getFilterKey(filter);

        if (!(key in loadingStore)) {
            const [invoiceCompanies, expenseCompanies] = splitCompanies(
                filter.companies,
            );

            if (type === "invoice") {
                const inv = filter.statuses.map((status) =>
                    loadingInvoices({ status, companies: invoiceCompanies }),
                );

                loadingStore[key] = computed(() =>
                    inv.map((i) => i.value).some((v) => v),
                );
            } else {
                const exp = filter.statuses.map((status) =>
                    loadingExpenses({ status, companies: expenseCompanies }),
                );

                loadingStore[key] = computed(() =>
                    exp.map((e) => e.value).some((v) => v),
                );
            }
        }

        return loadingStore[key];
    };

    const loaded = (filter: ApproveFilter, type: ApproveEntityType) => {
        const key = type + getFilterKey(filter);

        if (!(key in loadedStore)) {
            const [invoiceCompanies, expenseCompanies] = splitCompanies(
                filter.companies,
            );

            if (type === "invoice") {
                const inv = filter.statuses.map((status) =>
                    loadedInvoices({ status, companies: invoiceCompanies }),
                );

                loadedStore[key] = computed(() =>
                    inv.map((i) => i.value).every((v) => v),
                );
            } else {
                const exp = filter.statuses.map((status) =>
                    loadedExpenses({ status, companies: expenseCompanies }),
                );

                loadedStore[key] = computed(() =>
                    exp.map((e) => e.value).every((v) => v),
                );
            }
        }

        return loadedStore[key];
    };

    const approve = (params: ApproveParams, type: ApproveEntityType) =>
        type === "invoice" ? approveInvoice(params) : approveExpense(params);

    return {
        expenses,
        invoices,
        all,
        mine,
        listen,
        loading,
        loaded,
        approve,
        on,
        off,
    };
};
